<template>
    <div class="acesso-page">
        <b-container>
            <b-row align-v="center" class="min-vh-100">
                <b-col cols="12" md="8" lg="6" xl="5" class="mx-auto">
                    <!-- Logo e Título -->
                    <div class="text-center mb-4">
                        <img src="../assets/logo.png" alt="Logo" class="img-fluid mb-3" style="max-height: 80px">
                        <h1 class="h3 font-weight-bold text-dark mb-2">
                            Área do <span class="badge badge-success px-3 py-2" style="font-size: 1rem;">Cliente</span>
                        </h1>
                        <p class="text-secondary">Acompanhe seu contrato de forma simples e segura</p>
                    </div>

                    <!-- Card de Login -->
                    <b-card class="shadow-lg border-0">
                        <b-card-title class="text-center mb-4">
                            <h4 class="mb-0">Acesse sua área</h4>
                        </b-card-title>

                        <b-form @submit.prevent="acessar">
                            <!-- Código da Ficha -->
                            <b-form-group
                                label="Código da ficha"
                                label-for="codigo"
                                :invalid-feedback="$v.dados.codigo.$error ? 'Campo obrigatório' : ''"
                                :state="$v.dados.codigo.$dirty ? !$v.dados.codigo.$error : null"
                            >
                                <b-form-input
                                    id="codigo"
                                    v-model.trim="$v.dados.codigo.$model"
                                    v-mask="'###########'"
                                    placeholder="Digite o código da ficha"
                                    :state="$v.dados.codigo.$dirty ? !$v.dados.codigo.$error : null"
                                    size="lg"
                                />
                            </b-form-group>

                            <!-- CPF ou CNPJ -->
                            <b-form-group
                                label="CPF ou CNPJ"
                                label-for="cpf"
                                :invalid-feedback="mensagemErroDocumento"
                                :state="$v.dados.cpf.$dirty ? !$v.dados.cpf.$error : null"
                            >
                                <b-form-input
                                    id="cpf"
                                    v-model.trim="$v.dados.cpf.$model"
                                    v-mask="mascaraDocumentoDinamica"
                                    placeholder="000.000.000-00 ou 00.000.000/0000-00"
                                    :state="$v.dados.cpf.$dirty ? !$v.dados.cpf.$error : null"
                                    size="lg"
                                />
                            </b-form-group>

                            <!-- Data de nascimento (apenas para CPF) -->
                            <b-form-group
                                v-show="exibirCampoDataNascimento"
                                label="Data de nascimento"
                                label-for="dt_nascimento"
                                :invalid-feedback="mensagemErroData"
                                :state="$v.dados.dt_nascimento.$dirty ? !$v.dados.dt_nascimento.$error : null"
                            >
                                <b-form-input
                                    id="dt_nascimento"
                                    v-model.trim="$v.dados.dt_nascimento.$model"
                                    v-mask="'##/##/####'"
                                    placeholder="DD/MM/AAAA"
                                    :state="$v.dados.dt_nascimento.$dirty ? !$v.dados.dt_nascimento.$error : null"
                                    size="lg"
                                />
                            </b-form-group>

                            <!-- Botão de Acesso -->
                            <b-button
                                type="submit"
                                variant="primary"
                                size="lg"
                                block
                                class="mt-4"
                                :disabled="carregando"
                            >
                                <b-spinner small v-if="carregando" class="mr-2"></b-spinner>
                                <b-icon-box-arrow-in-right v-else class="mr-2" />
                                {{ carregando ? 'Verificando...' : 'Acessar Minha Área' }}
                            </b-button>
                        </b-form>

                        <!-- Rodapé do Card -->
                        <div class="text-center mt-4 pt-3 border-top">
                            <small class="text-muted d-flex align-items-center justify-content-center">
                                <b-icon-shield-lock class="mr-2 text-success"></b-icon-shield-lock>
                                <span>Seus dados estão protegidos com segurança</span>
                            </small>
                        </div>
                    </b-card>
                </b-col>
            </b-row>
        </b-container>
    </div>
  </template>
<script>
import { mapActions } from 'vuex'

import { required, requiredIf } from 'vuelidate/lib/validators'

import api from '@/api/app.api.js'
import { normalizarDigitos, documentoValido, mascaraDocumentoLogin } from '@/utils/documentoBrasil.js'

// Validador customizado: verifica se a data DD/MM/AAAA é válida
const dataValida = (valor) => {
  if (!valor) return false
  if (valor.length !== 10) return false

  const partes = valor.split('/')
  if (partes.length !== 3) return false

  const dia = parseInt(partes[0], 10)
  const mes = parseInt(partes[1], 10)
  const ano = parseInt(partes[2], 10)

  if (isNaN(dia) || isNaN(mes) || isNaN(ano)) return false
  if (mes < 1 || mes > 12) return false
  if (dia < 1 || dia > 31) return false
  if (ano < 1900 || ano > new Date().getFullYear()) return false

  // Verifica se a data realmente existe (ex: 31/02 não existe)
  const dataObj = new Date(ano, mes - 1, dia)
  return dataObj.getFullYear() === ano &&
               dataObj.getMonth() === mes - 1 &&
               dataObj.getDate() === dia
}

const documentoCompletoValido = (valor) => documentoValido(valor)

export default {
  name: 'Acesso',
  components: {
  },
  data () {
    return {
      dados: {
        codigo: '',
        cpf: '',
        dt_nascimento: ''
      },
      carregando: false
    }
  },
  validations: {
    dados: {
      codigo: {
        required
      },
      cpf: {
        required,
        documentoCompletoValido
      },
      dt_nascimento: {
        required: requiredIf(function () {
          return normalizarDigitos(this.dados.cpf).length <= 11
        }),
        dataValida: function (valor) {
          if (normalizarDigitos(this.dados.cpf).length > 11) return true
          return dataValida(valor)
        }
      }
    }
  },
  computed: {
    mascaraDocumentoDinamica () {
      return mascaraDocumentoLogin(this.dados.cpf)
    },
    exibirCampoDataNascimento () {
      return normalizarDigitos(this.dados.cpf).length <= 11
    },
    mensagemErroDocumento () {
      if (!this.$v.dados.cpf.$dirty) return ''
      if (!this.$v.dados.cpf.required) return 'Campo obrigatório'
      if (!this.$v.dados.cpf.documentoCompletoValido) return 'Informe um CPF ou CNPJ válido'
      return ''
    },
    mensagemErroData () {
      if (!this.$v.dados.dt_nascimento.$dirty) return ''
      if (!this.$v.dados.dt_nascimento.required) return 'Campo obrigatório'
      if (!this.$v.dados.dt_nascimento.dataValida) return 'Data inválida. Use o formato DD/MM/AAAA'
      return ''
    }
  },
  mounted () {
    console.log('Acesso.vue mounted')
  },
  watch: {
    'dados.cpf' (val) {
      if (normalizarDigitos(val).length > 11) {
        this.dados.dt_nascimento = ''
      }
    }
  },
  methods: {
    ...mapActions('msgErro', { exibirMsgErro: 'exibirMsgErro' }),
    ...mapActions('msgSucesso', { exibirMsgSucesso: 'exibirMsgSucesso' }),
    ...mapActions('auth', ['setAuth']),

    acessar () {
      this.$v.$touch()
      if (!this.$v.$invalid) {
        this.carregando = true

        const doc = normalizarDigitos(this.dados.cpf)
        const dadosLimpos = {
          codigo: this.dados.codigo,
          cpf: doc
        }
        if (doc.length === 11) {
          const partesData = this.dados.dt_nascimento.split('/')
          dadosLimpos.dt_nascimento = partesData[2] + '-' + partesData[1] + '-' + partesData[0]
        }

        api.login(dadosLimpos)
          .then(response => {
            if (!response.data || response.data.status !== 'ok') {
              this.exibirMsgErro('Dados inválidos')
              this.carregando = false
              return
            }

            // Salvar token e dados da ficha no Vuex
            this.setAuth({
              token: response.data.token,
              expires_in: response.data.expires_in,
              ficha: response.data.ficha
            })

            this.exibirMsgSucesso('Acesso realizado com sucesso!')

            // Redirecionar para contrato
            setTimeout(() => {
              this.$router.push({
                name: 'Contrato',
                params: response.data.ficha
              })
            }, 500)
          })
          .catch(error => {
            console.error(error)
            const data = error.response && error.response.data
            let mensagem = 'Dados inválidos ou contrato não encontrado'
            if (data) {
              if (data.error) mensagem = data.error
              else if (data.message && data.errors) {
                const firstKey = Object.keys(data.errors)[0]
                if (firstKey && data.errors[firstKey] && data.errors[firstKey][0]) {
                  mensagem = data.errors[firstKey][0]
                } else {
                  mensagem = data.message
                }
              } else if (data.message) mensagem = data.message
            }
            this.exibirMsgErro(mensagem)
            this.carregando = false
          })
      } else {
        this.exibirMsgErro('Preencha todos os campos corretamente')
      }
    }
  }
}
</script>

<style scoped>
.acesso-page {
    background-color: #f0f0f0;
    min-height: 100vh;
    padding: 20px;
}

.card {
    border: 1px solid #e0e0e0;
    border-radius: 16px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
    background-color: #ffffff;
}

.card-title h4 {
    color: #333333;
    font-weight: 600;
}

.btn-primary {
    background-color: #39b54a;
    border-color: #39b54a;
    font-weight: 600;
    min-height: 48px;
    transition: all 0.3s ease;
}

.btn-primary:hover:not(:disabled) {
    background-color: #2d8f3b;
    border-color: #2d8f3b;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(57, 181, 74, 0.3);
}

.btn-primary:focus {
    box-shadow: 0 0 0 0.2rem rgba(57, 181, 74, 0.25);
}

.btn-primary:disabled {
    opacity: 0.7;
    cursor: not-allowed;
}

.badge-success {
    background-color: #39b54a;
}

/* Form labels com melhor contraste */
:deep(label) {
    color: #495057;
    font-weight: 500;
    margin-bottom: 0.5rem;
}

/* Form inputs */
:deep(.form-control) {
    border: 2px solid #e0e0e0;
    border-radius: 8px;
    transition: all 0.2s ease;
}

:deep(.form-control:focus) {
    border-color: #39b54a;
    box-shadow: 0 0 0 0.2rem rgba(57, 181, 74, 0.15);
}

/* Rodapé do card */
.text-muted {
    color: #6c757d !important;
}

/* Responsividade mobile */
@media (max-width: 576px) {
    .acesso-page {
        padding: 10px;
    }

    .card {
        border-radius: 12px;
    }

    h1 {
        font-size: 1.5rem;
    }
}
</style>



// WEBPACK FOOTER //
// src/pages/Acesso.vue